import React, { useState, useRef, useEffect } from 'react';
import { Plus, Type, Barcode, Download, Trash2, Ruler } from 'lucide-react';
import './LabelDesign.css';

export default function BarcodeLabelDesigner() {
    const [labels, setLabels] = useState([{ id: 1, name: 'Label 1', elements: [], width: 800, height: 600 }]);
    const [activeLabel, setActiveLabel] = useState(0);
    const [selectedElement, setSelectedElement] = useState(null);
    const [draggedElement, setDraggedElement] = useState(null);
    const [jsBarcodeLoaded, setJsBarcodeLoaded] = useState(false);
    const [customWidth, setCustomWidth] = useState(800);
    const [customHeight, setCustomHeight] = useState(600);
    const canvasRef = useRef(null);
    const barcodeCanvasRefs = useRef({});

    // Standard label size templates
    const sizeTemplates = [
        // Small Product Labels
        { name: "Small Product (1.5×1\")", width: 144, height: 96, category: "product" },
        { name: "Medium Product (2×1\")", width: 192, height: 96, category: "product" },
        { name: "Large Product (2.5×1.5\")", width: 240, height: 144, category: "product" },

        // Standard Barcode Labels
        { name: "Small Barcode (1×0.75\")", width: 96, height: 72, category: "barcode" },
        { name: "Medium Barcode (1.5×1\")", width: 144, height: 96, category: "barcode" },
        { name: "Large Barcode (2×1.25\")", width: 192, height: 120, category: "barcode" },

        // Shipping Labels
        { name: "Small Shipping (4×3\")", width: 384, height: 288, category: "shipping" },
        { name: "Standard Shipping (4×6\")", width: 384, height: 576, category: "shipping" },
        { name: "Large Shipping (6×4\")", width: 576, height: 384, category: "shipping" },

        // Avery Standard Sizes
        { name: "Avery 5160 (1×2.625\")", width: 96, height: 252, category: "avery" },
        { name: "Avery 5163 (2×4\")", width: 192, height: 384, category: "avery" },
        { name: "Avery 5164 (3.33×4\")", width: 320, height: 384, category: "avery" },

        // Asset Tracking
        { name: "Small Asset (1×2\")", width: 96, height: 192, category: "asset" },
        { name: "Medium Asset (2×3\")", width: 192, height: 288, category: "asset" },
        { name: "Large Asset (3×5\")", width: 288, height: 480, category: "asset" },

        // Custom/Other
        { name: "Square (2×2\")", width: 192, height: 192, category: "custom" },
        { name: "Narrow (3×1\")", width: 288, height: 96, category: "custom" },
        { name: "Wide (6×2\")", width: 576, height: 192, category: "custom" }
    ];

    // Group templates by category for dropdown organization
    const groupedSizeTemplates = [
        {
            label: "Product Labels",
            options: sizeTemplates.filter(t => t.category === "product")
        },
        {
            label: "Barcode Labels",
            options: sizeTemplates.filter(t => t.category === "barcode")
        },
        {
            label: "Shipping Labels",
            options: sizeTemplates.filter(t => t.category === "shipping")
        },
        {
            label: "Avery Standards",
            options: sizeTemplates.filter(t => t.category === "avery")
        },
        {
            label: "Asset Tracking",
            options: sizeTemplates.filter(t => t.category === "asset")
        },
        {
            label: "Custom Shapes",
            options: sizeTemplates.filter(t => t.category === "custom")
        }
    ];

    // Load JsBarcode library
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js';
        script.async = true;
        script.onload = () => {
            setJsBarcodeLoaded(true);
        };
        document.body.appendChild(script);

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, []);

    // Update custom dimensions when label changes
    useEffect(() => {
        setCustomWidth(labels[activeLabel].width);
        setCustomHeight(labels[activeLabel].height);
    }, [activeLabel, labels]);

    const addElement = (type) => {
        const newElement = {
            id: Date.now(),
            type,
            x: 50,
            y: 50,
            content: type === 'text' ? 'Sample Text' : '123456789',
            fontSize: 16,
            fontWeight: 'normal',
            width: type === 'barcode' ? 200 : 120,
            height: type === 'barcode' ? 100 : 30
        };

        const newLabels = [...labels];
        newLabels[activeLabel].elements.push(newElement);
        setLabels(newLabels);
        setSelectedElement(newElement.id);
    };

    const updateElement = (id, updates) => {
        const newLabels = [...labels];
        const element = newLabels[activeLabel].elements.find(e => e.id === id);
        if (element) {
            Object.assign(element, updates);
            setLabels(newLabels);
        }
    };

    const deleteElement = () => {
        if (!selectedElement) return;
        const newLabels = [...labels];
        newLabels[activeLabel].elements = newLabels[activeLabel].elements.filter(
            e => e.id !== selectedElement
        );
        setLabels(newLabels);
        setSelectedElement(null);
    };

    const addLabel = () => {
        setLabels([...labels, {
            id: Date.now(),
            name: `Label ${labels.length + 1}`,
            elements: [],
            width: 800,
            height: 600
        }]);
        setActiveLabel(labels.length);
    };

    const applySizeTemplate = (template) => {
        const newLabels = [...labels];
        newLabels[activeLabel] = {
            ...newLabels[activeLabel],
            width: template.width,
            height: template.height
        };
        setLabels(newLabels);
        setCustomWidth(template.width);
        setCustomHeight(template.height);
    };

    const applyCustomSize = () => {
        const width = Math.max(100, Math.min(2000, customWidth));
        const height = Math.max(100, Math.min(2000, customHeight));

        const newLabels = [...labels];
        newLabels[activeLabel] = {
            ...newLabels[activeLabel],
            width,
            height
        };
        setLabels(newLabels);
        setCustomWidth(width);
        setCustomHeight(height);
    };

    const downloadLabel = () => {
        const canvas = canvasRef.current;
        const link = document.createElement('a');
        link.download = `${labels[activeLabel].name}.png`;
        link.href = canvas.toDataURL();
        link.click();
    };

    const handleCanvasMouseDown = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const clickedElement = labels[activeLabel].elements.find(el => {
            if (el.type === 'text') {
                const ctx = canvasRef.current.getContext('2d');
                ctx.font = `${el.fontWeight} ${el.fontSize}px Arial`;
                const metrics = ctx.measureText(el.content);
                return x >= el.x && x <= el.x + metrics.width &&
                    y >= el.y - el.fontSize && y <= el.y;
            } else {
                return x >= el.x && x <= el.x + el.width &&
                    y >= el.y && y <= el.y + el.height;
            }
        });

        if (clickedElement) {
            setSelectedElement(clickedElement.id);
            setDraggedElement({
                id: clickedElement.id,
                offsetX: x - clickedElement.x,
                offsetY: y - clickedElement.y
            });
        } else {
            setSelectedElement(null);
        }
    };

    const handleMouseMove = (e) => {
        if (!draggedElement) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left - draggedElement.offsetX;
        const y = e.clientY - rect.top - draggedElement.offsetY;

        updateElement(draggedElement.id, { x, y });
    };

    const handleMouseUp = () => {
        setDraggedElement(null);
    };

    // Generate barcode using JsBarcode and convert to data URL
    const generateBarcodeDataURL = (value, width, height) => {
        return new Promise((resolve, reject) => {
            if (!window.JsBarcode) {
                reject(new Error('JsBarcode not loaded'));
                return;
            }

            try {
                const canvas = document.createElement('canvas');

                window.JsBarcode(canvas, value, {
                    format: "CODE128",
                    width: 2,
                    height: Math.max(40, height - 30),
                    displayValue: true,
                    fontSize: 14,
                    margin: 5,
                    background: "#ffffff",
                    lineColor: "#000000"
                });

                resolve(canvas.toDataURL());
            } catch (error) {
                console.error('Barcode generation error:', error);
                reject(error);
            }
        });
    };

    useEffect(() => {
        const renderCanvas = async () => {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');

            // Clear canvas
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Draw border
            ctx.strokeStyle = '#e5e7eb';
            ctx.lineWidth = 1;
            ctx.strokeRect(0, 0, canvas.width, canvas.height);

            // Draw grid for better positioning
            ctx.strokeStyle = '#f3f4f6';
            ctx.lineWidth = 0.5;
            const gridSize = 20;
            for (let x = 0; x < canvas.width; x += gridSize) {
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, canvas.height);
                ctx.stroke();
            }
            for (let y = 0; y < canvas.height; y += gridSize) {
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(canvas.width, y);
                ctx.stroke();
            }

            // Draw all elements
            for (const element of labels[activeLabel].elements) {
                if (element.type === 'text') {
                    ctx.font = `${element.fontWeight} ${element.fontSize}px Arial`;
                    ctx.fillStyle = 'black';
                    ctx.fillText(element.content, element.x, element.y);

                    // Draw selection border
                    if (element.id === selectedElement) {
                        const metrics = ctx.measureText(element.content);
                        ctx.strokeStyle = '#3b82f6';
                        ctx.lineWidth = 2;
                        ctx.strokeRect(
                            element.x - 4,
                            element.y - element.fontSize - 2,
                            metrics.width + 8,
                            element.fontSize + 4
                        );
                    }
                } else if (element.type === 'barcode' && jsBarcodeLoaded) {
                    try {
                        // Generate barcode using JsBarcode
                        const barcodeDataURL = await generateBarcodeDataURL(
                            element.content || '123456789',
                            element.width,
                            element.height
                        );

                        const img = new Image();
                        await new Promise((resolve) => {
                            img.onload = resolve;
                            img.src = barcodeDataURL;
                        });

                        ctx.drawImage(img, element.x, element.y, element.width, element.height);

                        // Draw selection border
                        if (element.id === selectedElement) {
                            ctx.strokeStyle = '#3b82f6';
                            ctx.lineWidth = 2;
                            ctx.strokeRect(
                                element.x - 2,
                                element.y - 2,
                                element.width + 4,
                                element.height + 4
                            );
                        }
                    } catch (error) {
                        // If barcode generation fails, show error text
                        ctx.fillStyle = 'red';
                        ctx.font = '12px Arial';
                        ctx.fillText('Invalid barcode', element.x, element.y + 20);
                    }
                }
            }
        };

        if (jsBarcodeLoaded) {
            renderCanvas();
        }
    }, [labels, activeLabel, selectedElement, jsBarcodeLoaded]);

    const selectedEl = labels[activeLabel].elements.find(e => e.id === selectedElement);
    const currentLabel = labels[activeLabel];

    return (
        <div className="barcode-designer-container">
            <div className="barcode-designer-wrapper">
                <h1 className="barcode-designer-title">Barcode Label Designer</h1>

                <div className="barcode-designer-layout">
                    <div className="barcode-designer-main">
                        <div className="barcode-designer-card">
                            

                            {/* Toolbar */}
                            <div className="toolbar-container">
                                <div className="toolbar-row">
                                    {/* Add element buttons */}
                                    <div className="element-buttons">
                                        <button
                                            onClick={() => addElement('text')}
                                            className="element-button"
                                        >
                                            <Type className="icon-md" />
                                            <div>Add Text</div>
                                        </button>
                                        <button
                                            onClick={() => addElement('barcode')}
                                            disabled={!jsBarcodeLoaded}
                                            className={`element-button ${!jsBarcodeLoaded ? 'element-button-disabled' : ''}`}
                                        >
                                            <Barcode className="icon-md" />
                                            <div>{jsBarcodeLoaded ? 'Add Barcode' : 'Loading...'}</div>
                                        </button>
                                    </div>

                                    {/* Size controls */}
                                    <div className="size-controls">
                                        {/* Size Template Dropdown */}
                                        <div className="size-dropdown-container">
                                            <label className="size-dropdown-label">
                                                Label Size:
                                            </label>
                                            <select
                                                onChange={(e) => {
                                                    const selectedTemplate = sizeTemplates.find(t =>
                                                        `${t.width}x${t.height}` === e.target.value
                                                    );
                                                    if (selectedTemplate) {
                                                        applySizeTemplate(selectedTemplate);
                                                    }
                                                }}
                                                value={`${currentLabel.width}x${currentLabel.height}`}
                                                className="size-dropdown"
                                            >
                                                <option value="">Custom</option>
                                                {groupedSizeTemplates.map((group, index) => (
                                                    <optgroup key={index} label={group.label}>
                                                        {group.options.map((template) => (
                                                            <option
                                                                key={`${template.width}x${template.height}`}
                                                                value={`${template.width}x${template.height}`}
                                                            >
                                                                {template.name} ({template.width}×{template.height}px)
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Custom Size Inputs */}
                                        <div className="custom-size-container">
                                            <div className="size-input-group">
                                                <label className="size-input-label">W:</label>
                                                <input
                                                    type="number"
                                                    value={customWidth}
                                                    onChange={(e) => setCustomWidth(parseInt(e.target.value) || 100)}
                                                    className="size-input"
                                                    min="100"
                                                    max="2000"
                                                />
                                            </div>
                                            <span className="size-separator">×</span>
                                            <div className="size-input-group">
                                                <label className="size-input-label">H:</label>
                                                <input
                                                    type="number"
                                                    value={customHeight}
                                                    onChange={(e) => setCustomHeight(parseInt(e.target.value) || 100)}
                                                    className="size-input"
                                                    min="100"
                                                    max="2000"
                                                />
                                            </div>
                                            <button
                                                onClick={applyCustomSize}
                                                className="size-apply-button"
                                            >
                                                Apply
                                            </button>
                                        </div>

                                        <button
                                            onClick={downloadLabel}
                                            className="export-button"
                                        >
                                            <Download className="icon-sm" />
                                            Export PNG
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Canvas area */}
                            <div className="canvas-container">
                                <div className="canvas-header">
                                    <span>Canvas Size: {currentLabel.width} × {currentLabel.height} pixels</span>
                                    <span className="canvas-size-inches">
                                        {Math.round((currentLabel.width / 96) * 10) / 10} × {Math.round((currentLabel.height / 96) * 10) / 10} inches
                                    </span>
                                </div>
                                <canvas
                                    ref={canvasRef}
                                    width={currentLabel.width}
                                    height={currentLabel.height}
                                    onMouseDown={handleCanvasMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseLeave={handleMouseUp}
                                    className="design-canvas"
                                />
                            </div>
                            {/* Label tabs */}
                            <div className="label-tabs-container">
                                {labels.map((label, idx) => (
                                    <button
                                        key={label.id}
                                        onClick={() => setActiveLabel(idx)}
                                        className={`label-tab ${idx === activeLabel ? 'label-tab-active' : 'label-tab-inactive'}`}
                                    >
                                        {label.name}
                                    </button>
                                ))}
                                <button
                                    onClick={addLabel}
                                    className="label-tab-add"
                                >
                                    <Plus className="icon-sm" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="barcode-designer-sidebar">
                        <div className="properties-panel">
                            {selectedEl ? (
                                <div className="properties-content">
                                    <div className="property-group">
                                        <label className="property-label">X Position</label>
                                        <input
                                            type="number"
                                            value={Math.round(selectedEl.x)}
                                            onChange={(e) => updateElement(selectedEl.id, { x: parseInt(e.target.value) || 0 })}
                                            className="property-input"
                                        />
                                    </div>

                                    <div className="property-group">
                                        <label className="property-label">Y Position</label>
                                        <input
                                            type="number"
                                            value={Math.round(selectedEl.y)}
                                            onChange={(e) => updateElement(selectedEl.id, { y: parseInt(e.target.value) || 0 })}
                                            className="property-input"
                                        />
                                    </div>

                                    {selectedEl.type === 'text' ? (
                                        <>
                                            <div className="property-group">
                                                <label className="property-label">Text Content</label>
                                                <input
                                                    type="text"
                                                    value={selectedEl.content}
                                                    onChange={(e) => updateElement(selectedEl.id, { content: e.target.value })}
                                                    className="property-input"
                                                />
                                            </div>

                                            <div className="property-group">
                                                <label className="property-label">Font Size</label>
                                                <input
                                                    type="number"
                                                    value={selectedEl.fontSize}
                                                    onChange={(e) => updateElement(selectedEl.id, { fontSize: parseInt(e.target.value) || 12 })}
                                                    className="property-input"
                                                    min="8"
                                                    max="72"
                                                />
                                            </div>

                                            <div className="property-group">
                                                <label className="property-label">Font Weight</label>
                                                <select
                                                    value={selectedEl.fontWeight}
                                                    onChange={(e) => updateElement(selectedEl.id, { fontWeight: e.target.value })}
                                                    className="property-input"
                                                >
                                                    <option value="normal">Normal</option>
                                                    <option value="bold">Bold</option>
                                                </select>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="property-group">
                                                <label className="property-label">Barcode Value</label>
                                                <input
                                                    type="text"
                                                    value={selectedEl.content}
                                                    onChange={(e) => updateElement(selectedEl.id, { content: e.target.value })}
                                                    className="property-input"
                                                    placeholder="Enter barcode value"
                                                />
                                                <p className="property-hint">CODE128 format - letters & numbers</p>
                                            </div>

                                            <div className="property-group">
                                                <label className="property-label">Width</label>
                                                <input
                                                    type="number"
                                                    value={selectedEl.width}
                                                    onChange={(e) => updateElement(selectedEl.id, { width: parseInt(e.target.value) || 100 })}
                                                    className="property-input"
                                                    min="100"
                                                    max="500"
                                                />
                                            </div>

                                            <div className="property-group">
                                                <label className="property-label">Height</label>
                                                <input
                                                    type="number"
                                                    value={selectedEl.height}
                                                    onChange={(e) => updateElement(selectedEl.id, { height: parseInt(e.target.value) || 80 })}
                                                    className="property-input"
                                                    min="60"
                                                    max="300"
                                                />
                                            </div>
                                        </>
                                    )}

                                    <button
                                        onClick={deleteElement}
                                        className="delete-button"
                                    >
                                        <Trash2 className="icon-sm" />
                                        Delete Element
                                    </button>
                                </div>
                            ) : (
                                <div className="properties-empty">
                                    <p className="empty-message">Select an element to edit its properties</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}